import React, { createContext, useContext, useEffect, useState } from "react";

export type HandleTheme = "dark" | "light";

type ThemeContextValue = {
  theme: HandleTheme;
  setTheme: (next: HandleTheme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = "handle.theme";

function initialTheme(): HandleTheme {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<HandleTheme>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = theme;
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Private browsing can disable storage; the switch remains functional.
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      toggleTheme: () => setTheme(current => current === "dark" ? "light" : "dark"),
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useHandleTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useHandleTheme must be used within ThemeProvider");
  return context;
}
